import { action } from '@reatom/framework'
import { match } from 'ts-pattern'

import { identity, pipe, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { premiumMetaUpdateReceived } from '@root/entities/premium/atoms'
import { TxSerialized } from '@root/entities/transaction'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { Error } from '@root/shared'
import { ApiError, apiRequest, type PremiumApi } from '@root/shared/api'
import { PremiumErrorCode } from '@root/shared/api/api-error-code'

import type { TypeHelper } from '@blade/std-lib'
import type {
  PremiumInitiatePurchaseCreateData,
  PremiumInitiatePurchaseCreatePayload,
  PremiumVerifyPurchaseCreatePayload,
} from '@blade/trade-api-client'
import type { CurrencyAddress } from '@root/entities/currency'
import type { WalletId } from '@root/entities/wallet'
import type { ServicesApi } from '@root/shared/api'

export type InitiateBoosterPurchaseResponse = PremiumInitiatePurchaseCreateData

export const initiateBoostersPurchaseRequestAction = action(
  async (
    ctx,
    request: TypeHelper.OmitKey<
      PremiumInitiatePurchaseCreatePayload,
      'network'
    > & {
      network: Network.Network
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.premium.premiumInitiatePurchaseCreate, [
        {
          boostersAmount: request.boostersAmount,
          tokenAddress: request.tokenAddress,
          type: request.type,
          walletId: request.walletId,
          network: Network.toDto(request.network),
        },
      ]),
    )

    const mappedResult = pipe(
      result,

      Result.map((value) => ({
        ...value,
        serializedTransaction: TxSerialized(value.serializedTransaction),
      })),
      Result.mapError((e) =>
        match(e)
          .when(
            ApiError.isOfCodes([
              PremiumErrorCode.PremiumNotFound,
              PremiumErrorCode.PremiumAlreadyPurchased,
            ]),
            identity,
          )
          .otherwise(() => e),
      ),
    )

    return mappedResult
  },
  'initiateBoostersPurchaseRequestAction',
)

export type VerifyBoosterPurchaseResponse = {
  currency: ServicesApi.TokenMetaDto
  premium: ServicesApi.PremiumMetaDto
}

export const verifyBoostersPurchaseRequestAction = action(
  async (ctx, request: PremiumVerifyPurchaseCreatePayload) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.premium.premiumVerifyPurchaseCreate, [
        {
          verificationId: request.verificationId,
          type: request.type,
          portalLink: request.portalLink,
          network: request.network,
          signedTx: request.signedTx,
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.mapError((e) =>
        match(e)
          .when(
            ApiError.isOfCodes([
              PremiumErrorCode.PremiumNotFound,
              PremiumErrorCode.TransactionNotFound,
              PremiumErrorCode.NotEnoughPaid,
            ]),
            identity,
          )
          .otherwise(() => e),
      ),
      Result.map(
        (it): VerifyBoosterPurchaseResponse => ({
          currency: it,
          premium: it.premium,
        }),
      ),
    )

    if (Result.isSuccess(mappedResult)) {
      premiumMetaUpdateReceived(ctx, {
        currency: mappedResult.success.currency,
        premiumMeta: mappedResult.success.premium,
      })
    }

    return mappedResult
  },
  'verifyBoostersPurchaseRequestAction',
)

export type ProcessBoostersPurchaseRequest = {
  type: PremiumApi.PurchaseTypeDto
  portalLink: string | undefined
  network: Network.Network
  walletId: WalletId
  boosters: number
  currencyAddress: CurrencyAddress
}

export const processBoostersPurchaseAction = action(
  async (ctx, request: ProcessBoostersPurchaseRequest) => {
    const { secureStorageService } = ctx.get(dependenciesAtom)

    const instructionsResult = await ctx.schedule(() =>
      initiateBoostersPurchaseRequestAction(ctx, {
        boostersAmount: request.boosters,
        tokenAddress: request.currencyAddress,
        type: request.type,
        network: request.network,
        walletId: request.walletId,
      }),
    )

    if (Result.isFailure(instructionsResult)) {
      return Result.fail(instructionsResult.failure)
    }

    const signedTxResult = await ctx.schedule(() =>
      secureStorageService.signTransaction({
        tx: instructionsResult.success.serializedTransaction,
        operation: 'booster',
        network: Network.fromDto(request.network),
        walletId: request.walletId,
        password: undefined,
        // TODO: accept valid amount used in transaction.
        amount: '0',
      }),
    )

    if (Result.isFailure(signedTxResult)) {
      return Result.fail(Error.UnknownError.fromUnknown(signedTxResult))
    }

    const result = await ctx.schedule(() =>
      verifyBoostersPurchaseRequestAction(ctx, {
        network: Network.toDto(request.network),
        portalLink: request.portalLink ?? '',
        type: request.type,
        verificationId: instructionsResult.success.verificationId,
        signedTx: signedTxResult.success.txSigned,
      }),
    )

    return result
  },
  'processBoostersPurchaseAction',
)
